import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SharedTable from '../../../../../shared/Table/SharedTable';
import UnifiedDateRangePicker from './UnifiedDateRangePicker';
import { formatDate } from '../../../../../../utils/dateUtils';
import '../../../../../../components/shared/filterStyles.css';

/**
 * JSDoc for TimesheetsDetailView component.
 * 
 * @component
 * @param {Object} props - Component properties.
 * @param {Array} props.data - Array of timesheet entries.
 * @param {boolean} props.isLoading - Loading state flag.
 * @returns {React.ReactElement} The timesheet detail view.
 */
export default function TimesheetsDetailView({ data = [], isLoading = false, project }) {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });

    const hasActiveFilters = !!(searchText || dateRange.start || dateRange.end);

    const filteredData = useMemo(() => {
        return data.filter(item => {
            let matchesText = true;
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const formattedDate = formatDate(item.entry_date).toLowerCase();
                const formattedHours = `${parseFloat(item.hours).toFixed(2)} std.`;
                matchesText = (
                    (item.user_name && item.user_name.toLowerCase().includes(searchLower)) ||
                    (item.notes && item.notes.toLowerCase().includes(searchLower)) ||
                    formattedDate.includes(searchLower) ||
                    formattedHours.includes(searchLower)
                );
            }

            let matchesDate = true;
            if (dateRange.start || dateRange.end) {
                const itemDate = new Date(item.entry_date);
                itemDate.setHours(0, 0, 0, 0);

                if (dateRange.start) {
                    const start = new Date(dateRange.start);
                    start.setHours(0, 0, 0, 0);
                    matchesDate = matchesDate && itemDate >= start;
                }
                if (dateRange.end) {
                    const end = new Date(dateRange.end);
                    end.setHours(23, 59, 59, 999);
                    matchesDate = matchesDate && itemDate <= end;
                }
            }

            return matchesText && matchesDate;
        });
    }, [data, searchText, dateRange]);

    const handleReset = () => {
        setSearchText('');
        setDateRange({ start: '', end: '' });
    };

    const columns = [
        {
            key: 'entry_date',
            label: 'Datum',
            sortable: true,
            render: (ts) => formatDate(ts.entry_date),
            className: 'date-cell'
        },
        {
            key: 'user_name',
            label: 'Mitarbeiter',
            sortable: true,
            className: 'bold-cell'
        },
        {
            key: 'notes',
            label: 'Beschreibung / Notizen',
            sortable: true,
            render: (ts) => ts.notes || <span className="text-muted">-</span>,
            className: 'notes-cell'
        },
        {
            key: 'hours',
            label: 'Stunden',
            sortable: true,
            render: (ts) => `${parseFloat(ts.hours).toFixed(2)} Std.`,
            className: 'hours-cell align-right'
        }
    ];

    const handleRowClick = (item) => {
        if (project) {
            sessionStorage.setItem('projectModalBackup', JSON.stringify({
                isOpen: true,
                activeTab: 'analysis',
                currentMode: 'view',
                project: project
            }));
            sessionStorage.setItem('projectAnalysis_activeSubView', 'timesheets');
        }
        navigate(`/admin/tools`, {
            state: {
                selectedToolKey: 'timesheet',
                focusUserId: item.user_id,
                focusDate: item.entry_date
            }
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="co-filters-panel">
                <div className="co-filter-group">
                    <label>Suche</label>
                    <input
                        type="text"
                        className="co-filter-input"
                        placeholder="Nach Mitarbeiter oder Beschreibung filtern..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
                
                <UnifiedDateRangePicker
                    startDate={dateRange.start}
                    endDate={dateRange.end}
                    onChange={setDateRange}
                />

                <button
                    type="button"
                    className={`co-filter-reset-btn ${!hasActiveFilters ? 'disabled' : ''}`}
                    disabled={!hasActiveFilters}
                    onClick={handleReset}
                    title="Filter zurücksetzen"
                >
                    🔄
                </button>
            </div>

            <div className="subview-content-list glass-card">
                <SharedTable
                    data={filteredData}
                    columns={columns}
                    uniqueKeyField="id"
                    onRowClick={handleRowClick}
                    isLoading={isLoading}
                    emptyMessage="Keine Stundenbuchungen für die gewählten Filter gefunden."
                />
            </div>
        </div>
    );
}
